import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import cn from 'classnames';
import { ModalMode } from '../../../stores/Administrators/AdministratorModalStore';
import AccessCodeInfoStore, { getStatus, getGmtDateString }
    from '../../../stores/Policy/AccessCodes/AccessCodeInfoStore';
import PolicyStore from '../../../stores/Policy/PolicyStore';
import SnackStore from '../../../stores/SnackStore';
import { FuseConsoleField } from '../../ConsoleFragments';
import FuseDatePicker from '../../Datepicker/FuseDatePicker';
import Card from '../../Fuse/Card';
import FuseButton from '../../FuseControls/FuseButton';
import { ObservableFuseSelect } from '../../FuseControls/FuseSelect';
import FuseSwitch from '../../FuseControls/FuseSwitch';
import FuseTextfield from '../../FuseControls/FuseTextfield';
import { FTBodyNoColor as Body } from '../../Fuse';
import './AccessCodeInfo.css';

const block = b.lock('AccessCodeInfo');

class AccessCodesInfo extends Component {
    static propTypes = {
        policy: PolicyStore.PropType,
        store: AccessCodeInfoStore.PropType,
        mode: PropTypes.oneOf([ModalMode.CREATE, ModalMode.VIEW, ModalMode.DELETE, ModalMode.EDIT]),
        snack: SnackStore.PropType,
    };

    static EXP_DATE_TOOLTIP = 'Date the Activation Code can no longer be used. ' +
        'This will not affect previously used activations.';

    static LIMIT_TOOLTIP = 'The number of times the Activation Code can be used before becoming expired.'

    cleanupFns = [];

    componentDidMount() {
        const { policy, store } = this.props;

        this.cleanupFns.push(
            policy.browsingRoles.policyTable.addOnLoadListener((items) => {
                const noDefault = items.filter((r) => !r.isDefault);
                store.syncSelect(store.browsingRole)(noDefault);
            })
        );
        policy.browsingRoles.policyTable.load();
    }

    componentWillUnmount() {
        this.cleanupFns.map((fn) => fn());
    }

    getStatusDescription = () => {
        return getStatus(this.props.store.getValue()).name;
    }

    onCopyToClipboard = () => {
        const { store, snack } = this.props;

        if (store.copyTextToClipboard()) {
            snack.showSuccess(
                <span>Access code <b>{store.code.getValue()}</b> has been copied to your clipboard.</span>
            );
        }
    };

    renderConfig = () => {
        const { store, mode } = this.props;
        const {
            activationsAllowed,
            browsingRole,
            expirationDate,
            neverExpire,
            limitedActivations,
        } = store;

        return <Card title="Configure Code" className={block('Card')}>
            {mode === ModalMode.CREATE ? <ObservableFuseSelect
                singleSelection
                store={browsingRole}
                placeholder="Select"
                selectLabel="Browsing Role (required)"
                containerClassName={block('Select')}
                hintClassName={block('HintOfFuseSelect')}
                noOptionsLabel={'No browsing roles defined'}
            /> : null}
            <FuseConsoleField
                className={block('CardSection')}
                contentClassName={block('CardSectionContent')}
                name="Expiration Date"
                info={AccessCodesInfo.EXP_DATE_TOOLTIP}
                infoPosition="bottom"
            >
                <FuseSwitch
                    store={neverExpire}
                    label={neverExpire.isTrue ? 'Enabled' : 'Disabled'}
                />
            </FuseConsoleField>

            {neverExpire.isTrue ? <FuseDatePicker
                store={expirationDate}
                className={block('CardSection')}
                dateClassName={block('Date')}
                inputClassName={block('DatePicker')}
                label={'Code Valid Through (GMT)'}
            /> : null}

            <FuseConsoleField
                className={block('CardSection')}
                contentClassName={block('CardSectionContent')}
                name="Limited Activations"
                info={AccessCodesInfo.LIMIT_TOOLTIP}
                infoPosition="bottom"
            >
                <FuseSwitch
                    store={limitedActivations}
                    label={limitedActivations.isTrue ? 'Enabled' : 'Disabled'}
                />
            </FuseConsoleField>
            {limitedActivations.isTrue ? <div className={block('ActLimitWrapper')}>
                <FuseTextfield
                    label="Activation Limit"
                    store={activationsAllowed}
                    type="number"
                    className={cn(block('ActLimit'))}
                    error={activationsAllowed.errorMessage !== ''}
                    bottomText={activationsAllowed.errorMessage}
                    min={0}
                    max={AccessCodeInfoStore.MAX_ACTIVATION_LIMIT}
                />
            </div> : null}
        </Card>;
    }

    renderSummary = () => {
        const { store, mode } = this.props;
        const {
            activations,
            activationsAllowed,
            limitedActivations,
            browsingRole,
            code,
            expirationDate,
            neverExpire,
        } = store;

        return <Card title="Summary" className={block('Card')}>
            <FuseConsoleField
                name="Code"
                contentClassName={block('CodeWrapper')}
            >
                <span>{code.getValue()}
                    {mode === ModalMode.EDIT || mode === ModalMode.VIEW ? <FuseButton
                        className={block('CopyToClipBoard')}
                        icon="assignment"
                        type="tertiary"
                        color="blue"
                        onClick={this.onCopyToClipboard}
                    ><Body> Copy to clipboard </Body></FuseButton> : null}
                </span>

            </FuseConsoleField>
            <FuseConsoleField name="Status" className={block('CardSection')}>
                {this.getStatusDescription()}
            </FuseConsoleField>
            <FuseConsoleField name="Browsing Role" className={block('CardSection')}>
                {browsingRole.getLabelForSelectedValue()}
            </FuseConsoleField>
            <FuseConsoleField
                info={[ModalMode.VIEW, ModalMode.DELETE].includes(mode) ? AccessCodesInfo.EXP_DATE_TOOLTIP : null}
                infoPosition="right"
                name="Expiration Date"
                className={block('CardSection')}
            >
                {!neverExpire.isTrue ?
                    'Never Expires' :
                    getGmtDateString(expirationDate.getValue())
                }
            </FuseConsoleField>
            <FuseConsoleField
                info={[ModalMode.VIEW, ModalMode.DELETE].includes(mode) ? AccessCodesInfo.LIMIT_TOOLTIP : null}
                infoPosition="right"
                name="Activation Limit"
                className={block('CardSection')}
            >
                {!limitedActivations.isTrue ? 'Unlimited' : activationsAllowed.getValue()}
            </FuseConsoleField>
            <FuseConsoleField name="Activations" className={block('CardSection')}>
                {activations.getValue()}
            </FuseConsoleField>
        </Card>;
    }

    render() {
        const { mode } = this.props;

        return <Fragment>
            { (mode === ModalMode.CREATE || mode === ModalMode.EDIT) ? this.renderConfig() : null}
            { mode !== ModalMode.CREATE ? this.renderSummary() : null}
        </Fragment>;
    }
}

export default inject('snack', 'policy')(observer(AccessCodesInfo));
